/* ─────────────────────────────────────────────────────────────────────────
   Organisation branding — the vocabulary and the two derivations.

   This file is **owned here and copied verbatim into website-enterprise,
   website-cloud, scope and review** — every repository that renders a branded
   surface — the same arrangement `access.ts`, `entitlements.ts` and
   `handles.ts` have and for the same reason: several Workers and one native
   client have to answer the same question identically.

   Owned by website-main rather than by website-enterprise, which owns the
   `brand_*` columns and the screen that writes them, because this is the
   *derivation* rather than the data: the eleven lightness stops are measured
   off the violet ramp in `project/_ds/`, `productName` is defined against the
   shape of `shared/apps.ts`, and `genvectors-branding.test.ts` beside this
   file is what seven native repos compare their C++ port against.

   Propagate with `node scripts/sync-shared.mjs` from the workspace repo, never
   a hand `cp`; `shared/copies.test.ts` in each of the five pins it.

   Everything below is pure. No binding, no query, no `Date.now()`. A colour
   ramp derived differently in two places is a product that looks like two
   products, so the derivation is a function a test can pin rather than a
   convention each site follows.

   ── What an organisation may change, and what it may not ─────────────────
   It may change the **name**, the **word** in an app's chrome, one **accent
   colour** and a **logo**.

   It may not change teal, danger or warning. That is not a shortcut: it is
   the policy Compose already applies to author-editable course themes, and
   `coursepack.cpp` states the reason — a signal colour that means "live" or
   "complete" is semantic, and re-hueing it breaks meaning and contrast
   together. A customer whose brand is red does not get a product where
   "recording" and "error" are the same colour.

   Nor may it change anything that is an identifier rather than a
   presentation: reverse-DNS app ids, `APP_ORGANISATION`, update-feed
   origins, cookie names, database names. Those are listed in the workspace
   CLAUDE.md, and the admin screen does not offer them.
   ───────────────────────────────────────────────────────────────────────── */

// ── naming ──────────────────────────────────────────────────────────────────

/**
 * What an organisation calls the product.
 *
 * `null` throughout means "not set", and the fallbacks are the point: an
 * organisation that has configured nothing sees Mauvely, and one that has
 * configured only a colour still sees Mauvely's names.
 */
export interface Branding {
  /** "Northgate", or null. The company's own name. */
  name: string | null;
  /** The lowercase word an app's title bar draws, when the organisation wants
   *  one that is not the app's own. Rare — most set only `name`. */
  word: string | null;
  /** One `#rrggbb`. Null means Mauvely violet. */
  accent: string | null;
  /** Absolute URL, or null. */
  logoUrl: string | null;
}

export const NO_BRANDING: Branding = { name: null, word: null, accent: null, logoUrl: null };

/**
 * A product's display name under an organisation's branding.
 *
 * ```
 * brandName set   → `{brandName} {short}`  → "Northgate Mail"
 * brandName blank → `{short}`               → "Mail"
 * no branding     → `{fullName}`            → "Mauvely Mail"
 * ```
 *
 * One field, both looks, chosen per organisation. A company that wants its own
 * name on the product sets it; one that wants a neutral product leaves it blank
 * and gets bare product names, which is what the apps already draw in their own
 * chrome.
 *
 * `short` rather than stripping "Mauvely " off the front of `fullName`: the
 * registry already carries both, and "Sotto by Mauvely" is the entry that would
 * have made a prefix-strip produce "Sotto by Northgate".
 */
export function productName(
  branding: Branding | null,
  app: { name: string; short: string }
): string {
  if (!branding) return app.name;
  if (branding.name && branding.name.trim()) return `${branding.name.trim()} ${app.short}`;
  return app.short;
}

/** The word an app's chrome draws, lowercased the way `Wordmark` expects. */
export function chromeWord(branding: Branding | null, appShort: string): string {
  const word = branding?.word?.trim();
  return (word || appShort).toLowerCase();
}

// ── colour ──────────────────────────────────────────────────────────────────

/** `#rgb` or `#rrggbb`, case-insensitive. */
export const isHex = (v: unknown): v is string =>
  typeof v === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim());

/** `#rrggbb` → `[r, g, b]`, each 0–255. Accepts the three-digit form. */
export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.trim().replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16)
  ];
}

const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

export const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map((c) => clamp255(c).toString(16).padStart(2, '0')).join('')}`;

// ── sRGB ⇄ OKLab ────────────────────────────────────────────────────────────
// Björn Ottosson's transform. Used rather than HSL because HSL's "lightness" is
// not lightness: stepping a blue and a yellow by the same HSL amount produces
// two ramps that do not look like the same ramp, and a customer's ramp sitting
// beside Mauvely's teal has to be predictable.

const toLinear = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (v: number): number =>
  255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055);

export function rgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ];
}

export function oklabToRgb(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    clamp255(fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)),
    clamp255(fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s)),
    clamp255(fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s))
  ];
}

/**
 * The eleven lightness stops, measured off Mauvely's own violet ramp.
 *
 * Taken from the design system rather than spaced evenly, so a generated ramp
 * has the same rhythm as the one it replaces — evenly spaced stops produce a
 * ramp whose 100 is too dark for a soft fill and whose 900 is not dark enough
 * for text on it.
 */
export const RAMP_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

const RAMP_LIGHTNESS = [
  0.985, 0.945, 0.912, 0.851, 0.766, 0.663, 0.567, 0.471, 0.363, 0.263, 0.183
] as const;

/**
 * Chroma, as a fraction of the accent's own.
 *
 * The ends of a ramp are less saturated than its middle in every ramp the
 * design system ships. A ramp at constant chroma has a 50 that reads as a
 * colour rather than as a tint, and a 950 that vibrates against text.
 */
const RAMP_CHROMA = [0.18, 0.3, 0.5, 0.72, 0.9, 1.0, 0.98, 0.9, 0.78, 0.62, 0.48] as const;

export type Ramp = Record<(typeof RAMP_STOPS)[number], string>;

/**
 * Eleven stops from one colour, in OKLab.
 *
 * The accent's hue is kept and its chroma scaled; lightness comes from the
 * table above. So the customer's colour lands near stop 500 — which is where
 * `--brand` points — and everything else is derived around it.
 */
export function rampFrom(accentHex: string): Ramp {
  const [r, g, b] = hexToRgb(accentHex);
  const [, a1, b1] = rgbToOklab(r, g, b);

  const chroma = Math.hypot(a1, b1);
  // A grey accent has no hue to preserve. Anything derived from atan2 of two
  // near-zero numbers is noise, so the ramp is built as a neutral instead.
  const hue = chroma > 1e-6 ? Math.atan2(b1, a1) : 0;

  const out = {} as Ramp;
  RAMP_STOPS.forEach((stop, i) => {
    const c = chroma * RAMP_CHROMA[i]!;
    const [rr, gg, bb] = oklabToRgb(
      RAMP_LIGHTNESS[i]!,
      Math.cos(hue) * c,
      Math.sin(hue) * c
    );
    out[stop] = rgbToHex(rr, gg, bb);
  });
  return out;
}

// ── contrast ────────────────────────────────────────────────────────────────

const luminance = (r: number, g: number, b: number): number =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

/** WCAG contrast ratio between two hexes, 1–21. */
export function contrast(hexA: string, hexB: string): number {
  const [ar, ag, ab] = hexToRgb(hexA);
  const [br, bg, bb] = hexToRgb(hexB);
  const la = luminance(ar, ag, ab);
  const lb = luminance(br, bg, bb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** The two grounds a filled control has to survive. Light first. */
export const GROUNDS = { light: '#eceaf0', dark: '#161826' } as const;

export interface AccentVerdict {
  ok: boolean;
  /**
   * Why it was refused, or null.
   *
   * `grey` is the only refusal, and it is not about contrast — see below.
   */
  reason: 'grey' | null;
  /** Reported, not gated. See the note below on why these can only be facts. */
  lightRatio: number;
  darkRatio: number;
  /** How much colour is in the accent, 0–~0.37 in OKLab terms. Below the
   *  threshold the derived ramp is a grey ramp. */
  chroma: number;
}

/**
 * Roughly where a colour stops reading as a colour.
 *
 * Measured rather than reasoned about: `#f5f5f5` sits at 0.004 and `#0f9d58` at
 * 0.15. Anything under this produces a ramp a person would describe as grey.
 */
const MIN_CHROMA = 0.02;

/**
 * Whether an accent is usable.
 *
 * ── Contrast is not what this checks, and that is deliberate ────────────────
 * It was, and the check was dead. `rampFrom` *fixes* the lightness of every
 * stop — 600 always lands at OKLab L=0.567 — so the contrast of stop 600
 * against white does not depend on the hue that was typed. Every colour
 * measured, including `#ffff00`, `#ffffff` and `#000000`, came out between 4.2
 * and 4.8:1. A gate that cannot fail is worse than no gate: it tells an
 * administrator their colour was checked when nothing was.
 *
 * **Contrast is guaranteed by construction instead**, which is a stronger
 * property than validating one. The ratios are still returned, because a
 * number an admin can see is worth more than a promise they cannot.
 *
 * ── What can actually go wrong ──────────────────────────────────────────────
 * A colour with no colour in it. `#ffffff` and `#000000` both derive the same
 * grey ramp — the derivation keeps hue and lightness comes from the table, so a
 * white accent and a black one are indistinguishable. An administrator who
 * types their brand's white and gets a grey product has hit the one case where
 * the answer is surprising, so it is refused with the reason rather than
 * applied.
 */
export function checkAccent(accentHex: string): AccentVerdict {
  const [r, g, b] = hexToRgb(accentHex);
  const [, a1, b1] = rgbToOklab(r, g, b);
  const chroma = Math.hypot(a1, b1);

  const ramp = rampFrom(accentHex);
  const lightRatio = contrast(ramp[600], '#ffffff');
  const darkRatio = contrast(ramp[400], GROUNDS.dark);

  const grey = chroma < MIN_CHROMA;
  return { ok: !grey, reason: grey ? 'grey' : null, lightRatio, darkRatio, chroma };
}

// ── the CSS the browser applies ─────────────────────────────────────────────

/**
 * The custom properties an organisation's accent overrides.
 *
 * Only the violet ramp and the handful of tokens that are literals rather than
 * references to it. Everything else in the design system — `--brand`,
 * `--primary`, `--primary-soft`, `--text-link` and the rest — is already
 * `var(--violet-N)`, so it follows without being named here. That is why this
 * is a short list rather than a copy of the token file.
 *
 * Returned as a map rather than a stylesheet string so the caller can set them
 * on `documentElement` directly, which is what makes the change instant and
 * what keeps this usable from a native client that has no DOM.
 */
export function accentVariables(accentHex: string): Record<string, string> {
  const ramp = rampFrom(accentHex);
  const vars: Record<string, string> = {};
  for (const stop of RAMP_STOPS) vars[`--violet-${stop}`] = ramp[stop];

  const [r400, g400, b400] = hexToRgb(ramp[400]);
  const [r500, g500, b500] = hexToRgb(ramp[500]);
  const [r600, g600, b600] = hexToRgb(ramp[600]);

  // The tokens the design system writes as literal rgba rather than as a
  // reference. Missing these leaves a violet focus ring around a customer's
  // green buttons, which is the tell that a theme is half-applied.
  vars['--focus-ring'] = `rgba(${r600},${g600},${b600},0.45)`;
  vars['--selection-bg'] = ramp[200];
  vars['--primary-soft-rgb'] = `${r400},${g400},${b400}`;
  vars['--accent-soft-rgb'] = `${r500},${g500},${b500}`;

  return vars;
}
